import { translation } from '@/i18n';
import type { fr } from './fr';

export const en = translation<typeof fr>()({
  'seo.morocco': 'Morocco',
  'seo.list.title': 'Experiences and excursions in {where}',
  'seo.list.titleCategory': '{category} in {where} · Experiences',
  'seo.list.description':
    'Excursions, activities, tours and transfers in {where} with verified providers: price shown upfront, secure online payment and a voucher sent by email.',
  'seo.crumb.home': 'Home',
  'seo.notFound': 'Experience not found',
  'seo.checkout': 'Experience booking',
  'seo.booked': 'Experience booked',

  'gone.title': 'This experience is no longer available',
  'gone.text': 'Try again in a moment.',
  seeExperiences: 'See experiences',

  'hero.imageAlt': 'Camp in the Agafay desert',
  'hero.tag': 'New on Cardnd',
  'hero.lead': 'Morocco,',
  'hero.accent': 'up close.',
  'hero.text':
    'Desert, mountains, medinas: guided excursions and transfers booked online, at the price shown. Every outing is run by a verified provider.',
  'hero.securePayment': 'Secure online payment',
  'hero.voucher': 'Voucher sent by email',
  'hero.pickup': 'Pickup in {city}',
  'hero.trust.providers': 'Verified providers',
  'hero.trust.realtime': 'Real-time availability',
  'hero.trust.cars': 'Cars to rent on site',

  /* Hero card (ExperienceShowcase) */
  'showcase.live': 'Things to do · {city}',
  'showcase.open': 'See the experiences in {city}',
  'showcase.meta': '{duration} · {group}',
  'showcase.note': '<b>Card pre-authorised</b>, charged only when the provider confirms',

  'search.where': 'Where?',
  'search.when': 'When?',
  'search.category': 'Category',
  'search.duration': 'Duration',
  'search.addDate': 'Add a date',
  'search.allCategories': 'All categories',
  'search.flexible': 'Flexible dates',
  'search.submit': 'Search',
  'search.title': 'Where and when?',
  'search.destination': 'Search for a destination',
  'search.destinationLabel': 'Destination',
  'search.suggestions': 'Suggestions',
  'search.clear': 'Clear',
  'search.show': { one: 'Show {count} experience', other: 'Show {count} experiences' },
  'search.allDurations': 'All',
  'duration.court': '< 1h',
  'duration.moyen': '1 – 3h',
  'duration.long': '3 – 6h',
  'duration.journee': '6h +',

  'list.count': {
    one: '{count} <noun>experience</noun> departing from {city}',
    other: '{count} <noun>experiences</noun> departing from {city}',
  },
  'list.title': 'Choose your <serif>adventure.</serif>',
  'list.searchFilter': 'Search and filter',
  'list.clearSearch': 'Clear the search',
  'list.error.title': 'The experiences could not be loaded',
  'list.error.text': 'Check your connection, then try again.',
  'list.empty.title': 'No experiences match',
  'list.empty.text': 'Try another duration or another category.',
  'list.empty.clear': 'Clear the filters',
  'list.categories': 'Categories',

  'cars.eyebrow': 'Experience + car',
  'cars.title': 'Continue the trip by car',
  'cars.text': 'Rent a car from verified agencies in {city}, at the price shown, and travel at your own pace.',
  'cars.cta': 'See the cars',

  'days.loading': 'Loading dates',
  'days.label': 'Date',
  'day.full': 'Full',
  'day.low': 'Few left',
  'day.open': 'Open',
  'day.fullLabel': '{day}, full',
  'day.lowLabel': '{day}, few places left',
  'day.openLabel': '{day}, available',
  'times.label': 'Departure time',

  'detail.shellTitle': 'Experience',
  'detail.crumb': 'Breadcrumb',
  'detail.departure': 'Departure {time}',
  'detail.from': 'From',
  'detail.date': 'Date',
  'detail.slotsError': 'The departures could not be loaded.',
  'detail.full': 'Full on these dates: choose another date in the search.',
  'detail.participants': 'Participants',
  'detail.passengers': 'Passengers',
  'detail.fixedPerVehicle': 'Fixed price per vehicle · max. {max}',
  'detail.perPerson': '{price} / person',
  'detail.perPersonAt': '{price} / person for {count}',
  'detail.perChild': '{price} / child',
  'detail.childFree': 'Free',
  'detail.infantsHint': 'Under 2 · no seat',
  'detail.book': 'Book',
  'detail.bookTotal': 'Book · {total}',
  'detail.payNote': 'Full payment online · voucher sent by email',
  'detail.packs': 'Choose your option',
  'detail.packsLabel': 'Option',
  'detail.pack.pickup': 'Hotel pickup',
  'detail.pack.freeEntry': 'Free entry with proof',
  'detail.pack.popular': 'Most popular',
  'detail.addons': 'Extras',
  'detail.programme': 'What you’ll do',
  'detail.practical': 'Practical information',
  'detail.included': 'Included',
  'detail.excluded': 'Not included',
  'detail.bring': 'What to bring',
  'detail.meetingPoint': 'Meeting point',
  'detail.reviews': 'Reviews',
  'detail.noReviews': 'No reviews yet',
  'detail.noReviewsText': 'Travellers rate the experience after its date.',
  'detail.chooseDate': 'Choose a date',
  'detail.loading': 'Loading the experience',
  'detail.perGroup': '/ group',
  'detail.rating': 'Travellers’ rating',
  'detail.ratingCount': { one: '{count} review', other: '{count} reviews' },
  'detail.more.title': 'More experiences in <serif>{city}.</serif>',
  'detail.more.text': 'Discover other experiences offered in the same city.',
  'detail.more.all': 'All experiences',

  people: { one: '{count} person', other: '{count} people' },
  'bounds.upTo': { one: 'Up to {count} person', other: 'Up to {count} people' },
  'bounds.range': { one: 'From {min} to {count} person', other: 'From {min} to {count} people' },
  'party.adults': { one: '{count} adult', other: '{count} adults' },
  'party.passengers': { one: '{count} passenger', other: '{count} passengers' },
  'party.short': { one: '{count} person', other: '{count} people' },

  'checkout.incomplete': 'Incomplete selection',
  'checkout.chooseDate': 'Choose a date',
  'checkout.incompleteText': 'Choose a date and your participants on the experience page.',
  'checkout.saved': 'Booking saved',
  'checkout.steps': 'Booking steps',
  'checkout.step.experience': 'Experience',
  'checkout.step.booking': 'Booking & payment',
  'checkout.step.confirmation': 'Confirmation',
  'checkout.title': 'Just a few more <serif>details.</serif>',
  'checkout.lead': 'Secure online payment, voucher sent by email once confirmed.',
  'checkout.experience': 'Your experience',
  'checkout.recap.date': 'DATE',
  'checkout.recap.pack': 'OPTION',
  'checkout.recap.party': 'PARTICIPANTS',
  'checkout.recap.addons': 'EXTRAS',
  'checkout.recap.none': 'None',
  'checkout.dayGone': '<b>This departure is no longer available.</b> Choose another date on the experience page.',
  'checkout.contact': 'Your contact details',
  'checkout.field.firstName': 'First name *',
  'checkout.field.lastName': 'Last name *',
  'checkout.field.email': 'Email *',
  'checkout.field.phone': 'Phone',
  'checkout.voucherTo': 'Your booking voucher is sent to this address.',
  'checkout.account.title': 'An account is required',
  'checkout.account.text':
    'Sign in or create an account: your voucher and your messages with the provider are linked to it.',
  'checkout.terms':
    'I have read and accept the <terms>Terms and conditions</terms> and the <privacy>Privacy policy</privacy>.',
  'checkout.payment': 'Payment',
  'checkout.payFull': 'You pay the full amount online',
  'checkout.payFullText':
    'The total is pre-authorised on your card when you book, then charged when the provider confirms; your voucher is then sent by email. Nothing to pay on site.',
  'checkout.setByCardnd': 'Set by Cardnd',
  'checkout.today': 'Today, pre-authorised',
  'checkout.card': 'Bank card',
  'checkout.stripe.title': 'Secure Stripe card form',
  'checkout.stripe.text': 'It opens after “Pay”, with the exact amount pre-authorised on your card.',
  'checkout.fx':
    'Your card is charged in {currency}, at Cardnd’s rate at the time of payment: the exact amount is shown before the card is confirmed. The {currency} amounts on this page are indicative.',
  'checkout.pay': 'Pay',
  'checkout.payTotal': 'Pay {total}',
  'checkout.summary': 'Summary',
  'checkout.policy': 'If your plans change: <b>{cancellation}</b>',

  'blocker.day': 'This departure is no longer available: choose another date on the experience page.',
  'blocker.eligibility': 'Payment opens once the provider has accepted your free-entry request.',
  'blocker.pickup': 'Choose your pickup point.',
  'blocker.cutoff': 'Bookings for this departure are closed: choose another departure.',
  'blocker.quote': 'Calculating the price…',

  'error.firstName': 'Enter your first name.',
  'error.lastName': 'Enter your last name.',
  'error.email': 'Enter a valid email address.',
  'error.terms': 'Accept the terms and conditions to continue.',
  'error.answerRequired': 'An answer is required.',
  'error.answerChoice': 'Choose one of the suggested answers.',
  'error.answerTime': 'Enter a time in the HH:MM format.',
  'error.answerLength': '1,000 characters maximum.',
  'error.waiver': 'Accept the provider’s waiver.',
  'error.age': 'Age in years (0 to 120).',
  'error.minAge': { zero: 'Minimum age: {count}.', one: 'Minimum age: {count}.', other: 'Minimum age: {count}.' },
  'error.driverMinAge': {
    zero: 'Driver: minimum age {count}.',
    one: 'Driver: minimum age {count}.',
    other: 'Driver: minimum age {count}.',
  },
  'error.passengerMinAge': {
    zero: 'Passenger: minimum age {count}.',
    one: 'Passenger: minimum age {count}.',
    other: 'Passenger: minimum age {count}.',
  },
  'error.adultCompanion': 'An adult companion (18 or over) is required.',
  'error.height': 'Height in centimetres.',
  'error.minHeight': 'Minimum height: {cm} cm.',
  'error.weight': 'Weight in kilograms.',

  'details.questions': 'Questions from the provider',
  'details.choose': 'Choose…',
  'details.minAge': { zero: 'Minimum age {count}.', one: 'Minimum age {count}.', other: 'Minimum age {count}.' },
  'details.minHeight': 'Minimum height {cm} cm.',
  'details.adultCompanion': 'An adult aged 18 or over accompanies the group.',
  'details.default': 'The provider needs this information to prepare for your visit.',
  'details.drivers': 'Drivers',
  'details.perVehicle': { one: '{count} person per vehicle', other: '{count} people per vehicle' },
  'details.driverMinAge': { zero: 'minimum age {count}', one: 'minimum age {count}', other: 'minimum age {count}' },
  'details.driver': 'driver',
  'details.passenger': 'passenger',
  'details.age': 'Age *',
  'details.years': 'years',
  'details.height': 'Height *',
  'details.weight': 'Weight *',
  'details.weightReview': 'Above {kg} kg, the provider confirms participation before the card is charged.',
  'details.waiver': 'I accept the provider’s liability waiver.',
  'details.waiverDoc': 'I accept the provider’s liability waiver (<doc>read the document</doc>).',
  'participant.adult': 'Participant {n} · adult',
  'participant.child': 'Participant {n} · child',
  'participant.infant': 'Participant {n} · infant',

  'free.title': 'Request free entry',
  'free.signIn': '<link>Sign in</link> to send your request to the provider.',
  'free.intro': 'How many participants enter for free? The provider checks the proof before payment.',
  'free.adults': 'Free adults',
  'free.children': 'Free children',
  'free.infants': 'Free infants',
  'free.none': 'Enter at least one free participant.',
  'free.proof': 'Reference of the proof sent to the provider *',
  'free.proofRequired': 'Enter the reference of the proof.',
  'free.send': 'Send the request',
  'free.reading': 'Loading your request…',
  'free.pending': '<b>Request sent, waiting for the provider.</b> Payment opens as soon as they accept it.',
  'free.approved': '<b>Free entry granted.</b> The price shown takes it into account.',
  'free.used': 'This free entry has already been used for a booking: make a new request.',
  'free.rejected': '<b>Request declined.</b> {note}',
  'free.rejectedDefault': 'The proof was not accepted.',
  'free.refresh': 'Refresh',
  'free.restart': 'New request',

  'pickup.title': 'Pickup at your hotel or riad',
  'pickup.places': 'Pickup point',
  'pickup.included': 'Included',
  'pickup.address': 'Your hotel or riad address',
  'pickup.addressPlaceholder': 'Hotel name, street, neighbourhood',
  'pickup.addressRequired': 'Enter the pickup address.',
  'pickup.addressHint': 'The provider confirms the pickup at this address.',
  'pickup.useDevice': 'Use my location',
  'pickup.link': 'Or a Google Maps link to the hotel',
  'pickup.locate': 'Share your hotel’s location to check the pickup area.',
  'pickup.error.noGeo': 'Your device does not share its location: paste a Google Maps link instead.',
  'pickup.error.denied': 'Location unavailable: allow location access or paste a Google Maps link.',
  'pickup.error.badLink': 'This link contains no location: open the place in Google Maps, then copy the link.',
  'pickup.verdict.place': '{place}.',
  'pickup.verdict.placeFee': '{place} · {fee} surcharge.',
  'pickup.verdict.served': 'Address covered',
  'pickup.verdict.confirm': 'The provider will confirm the pickup at this address.',
  'pickup.verdict.outside': 'This address is outside the pickup area: choose another address.',

  'booked.shellTitle': 'Booking confirmed',
  'booked.notFound': 'Booking not found',
  'booked.notFoundText': 'Find all your bookings in My trips.',
  'booked.paid': 'Payment complete',
  'booked.confirming': 'Confirmation in progress',
  'booked.title': 'You’re booked.',
  'booked.titleNamed': 'You’re booked, <serif>{name}.</serif>',
  'booked.confirmed': 'Your voucher has been sent by email. Show it to your guide on the day.',
  'booked.confirmedTo': 'Your voucher has been sent to {email}. Show it to your guide on the day.',
  'booked.pending':
    'Your card is pre-authorised. The provider is confirming your booking: the charge happens then and your voucher is sent to you by email.',
  'booked.pendingTo':
    'Your card is pre-authorised. The provider is confirming your booking: the charge happens then and your voucher is sent to you at {email}.',
  'booked.cross': '<b>Need a car there?</b> Rent from verified agencies in {city}, at the price shown.',
  'booked.rentCar': 'Rent a car',
  'booked.trips': 'My trips',
  'booked.more': 'Other experiences',
  'booked.calendar': 'Add to calendar',
  'booked.help': 'Need help?',
  'booked.downloading': 'Downloading…',
  'booked.download': 'Download the voucher (PDF)',
  'booked.tickets.ready': 'Your entry tickets are ready.',
  'booked.tickets.time': 'Confirmed time: {time}. ',
  'booked.tickets.resent': 'Tickets resent by email',
  'booked.tickets.resend': 'Resend the tickets by email',
  'booked.loading': 'Loading the booking',

  'voucher.label': 'Experience voucher {ref}',
  'voucher.kicker': 'EXPERIENCE VOUCHER',
  'voucher.confirmed': 'Confirmed',
  'voucher.pending': 'Pending',
  'voucher.date': 'Date',
  'voucher.pin': 'PIN code',
  'voucher.qr': 'QR code of booking {ref}',
  'voucher.ref': 'Booking no.',
  'voucher.paid': 'Total paid',
  'voucher.held': 'Total pre-authorised',
});
